import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link, useParams, useNavigate } from "react-router-dom";
import AppShell from "../components/AppShell";
import FieldRenderer from "../components/workbook/FieldRenderer";
import DecisionGate from "../components/workbook/DecisionGate";
import ProgressRing from "../components/workbook/ProgressRing";
import {
  fetchWorksheet, fetchResponse, putResponse, submitResponse,
} from "../lib/workbookApi";
import { adaptCopy } from "../lib/copyAdaptApi";
import { useReadingLevel } from "../context/ReadingLevelContext";
import RegenerateChip from "../components/RegenerateChip";
import { formatError } from "../lib/api";
import { toast } from "sonner";
import {
  ArrowLeft, ArrowRight, ArrowLeftFromLine, Save, CheckCircle2, Loader2,
  Clock, Target, Link2, Lightbulb, Sparkles,
} from "lucide-react";

const AUTOSAVE_DEBOUNCE_MS = 2000;

/**
 * Split a long intro paragraph at the nearest sentence boundary so a wall of
 * text renders as 2-3 comfortable paragraphs. Preserves author double-newline
 * paragraph breaks (only splits within a paragraph that exceeds `maxLen`).
 */
function splitIntroCopy(text, maxLen = 350) {
  const paragraphs = String(text || "")
    .split(/\n\s*\n/)
    .map((p) => p.trim())
    .filter(Boolean);
  const out = [];
  for (const para of paragraphs) {
    if (para.length <= maxLen) { out.push(para); continue; }
    const sentences = para.match(/[^.!?]+[.!?]+["')\]]?(\s+|$)/g) || [para];
    let cur = "";
    for (const s of sentences) {
      if ((cur + s).length <= maxLen || !cur) {
        cur += s;
      } else {
        out.push(cur.trim());
        cur = s;
      }
    }
    if (cur.trim()) out.push(cur.trim());
  }
  return out;
}

// Lightweight metadata pills shown under the worksheet title.
function MetaPill({ icon: Icon, label, value }) {
  if (!value) return null;
  return (
    <div className="flex items-start gap-2 min-w-0">
      <Icon className="w-3.5 h-3.5 mt-1 text-muted-foreground shrink-0" />
      <div className="min-w-0">
        <div className="text-[10px] uppercase tracking-widest text-muted-foreground/85 font-medium">{label}</div>
        <div className="text-sm text-foreground/90 leading-tight break-words">{value}</div>
      </div>
    </div>
  );
}

function SavedIndicator({ state }) {
  // state: "idle" | "dirty" | "saving" | "saved" | "error"
  if (state === "saving") return (
    <span className="inline-flex items-center gap-1.5 text-xs text-muted-foreground"
          data-testid="autosave-state">
      <Loader2 className="w-3.5 h-3.5 animate-spin" /> Saving…
    </span>
  );
  if (state === "saved") return (
    <span className="inline-flex items-center gap-1.5 text-xs text-emerald-400"
          data-testid="autosave-state">
      <CheckCircle2 className="w-3.5 h-3.5" /> Saved
    </span>
  );
  if (state === "error") return (
    <span className="inline-flex items-center gap-1.5 text-xs text-red-400"
          data-testid="autosave-state">
      Autosave failed
    </span>
  );
  if (state === "dirty") return (
    <span className="inline-flex items-center gap-1.5 text-xs text-muted-foreground"
          data-testid="autosave-state">
      <Save className="w-3.5 h-3.5" /> Unsaved
    </span>
  );
  return null;
}

export default function WorkbookWorksheet() {
  const { worksheetId } = useParams();
  const nav = useNavigate();
  const { level: readingLevel } = useReadingLevel();
  const [ws, setWs] = useState(null);
  const [adaptedCopy, setAdaptedCopy] = useState({});
  const [adaptLoading, setAdaptLoading] = useState(false);
  const [answers, setAnswers] = useState({});
  const [notes, setNotes] = useState({});
  const [decision, setDecision] = useState({
    selected_option: "", commitment: "", review_date: "", other_text: "",
  });
  const [meta, setMeta] = useState({
    status: "not_started", completion_pct: 0, required_missing: [], word_count: 0,
    gate_complete: false, submitted_at: null,
  });
  const [autosave, setAutosave] = useState("idle");
  const [error, setError] = useState(null);
  const [highlightMissing, setHighlightMissing] = useState([]);
  const [submitting, setSubmitting] = useState(false);

  // Track pending patch so we only PUT changed keys — the merge contract on
  // the backend needs a partial payload, not the whole `answers` object.
  const pendingAnswers = useRef({});
  const pendingNotes = useRef({});
  const pendingDecision = useRef(null);
  const flushTimer = useRef(null);

  useEffect(() => {
    setWs(null); setAnswers({}); setNotes({}); setError(null); setHighlightMissing([]);
    pendingAnswers.current = {};
    pendingNotes.current = {};
    pendingDecision.current = null;

    Promise.all([fetchWorksheet(worksheetId), fetchResponse(worksheetId)])
      .then(([wsDoc, respDoc]) => {
        setWs(wsDoc);
        setAnswers(respDoc.answers || {});
        setNotes(respDoc.notes || {});
        setDecision({
          selected_option: respDoc.decision?.selected_option || "",
          commitment: respDoc.decision?.commitment || "",
          review_date: respDoc.decision?.review_date || "",
          other_text: respDoc.decision?.other_text || "",
        });
        setMeta({
          status: respDoc.status || "not_started",
          completion_pct: respDoc.completion_pct || 0,
          required_missing: respDoc.required_missing || [],
          word_count: respDoc.word_count || 0,
          gate_complete: !!respDoc.gate_complete,
          submitted_at: respDoc.submitted_at || null,
        });
      })
      .catch((e) => setError(formatError(e)));
  }, [worksheetId]);

  // Flush pending changes to the server. Called by the debounce timer and by
  // onBlur handlers. We only send what's dirty — the backend deep-merges.
  const flush = useCallback(async () => {
    const payload = {};
    if (Object.keys(pendingAnswers.current).length) {
      payload.answers = { ...pendingAnswers.current };
    }
    if (Object.keys(pendingNotes.current).length) {
      payload.notes = { ...pendingNotes.current };
    }
    if (pendingDecision.current) {
      payload.decision = { ...pendingDecision.current };
    }
    if (!payload.answers && !payload.decision && !payload.notes) return;
    pendingAnswers.current = {};
    pendingNotes.current = {};
    pendingDecision.current = null;
    setAutosave("saving");
    try {
      const updated = await putResponse(worksheetId, payload);
      setMeta({
        status: updated.status,
        completion_pct: updated.completion_pct,
        required_missing: updated.required_missing || [],
        word_count: updated.word_count || 0,
        gate_complete: !!updated.gate_complete,
        submitted_at: updated.submitted_at || null,
      });
      setAutosave("saved");
      setTimeout(() => setAutosave((s) => (s === "saved" ? "idle" : s)), 2200);
    } catch (e) {
      setAutosave("error");
      toast.error(formatError(e));
    }
  }, [worksheetId]);

  const scheduleFlush = useCallback(() => {
    if (flushTimer.current) clearTimeout(flushTimer.current);
    flushTimer.current = setTimeout(flush, AUTOSAVE_DEBOUNCE_MS);
    setAutosave("dirty");
  }, [flush]);

  // Beforeunload — flush synchronously if the user navigates away with
  // pending changes. Best-effort; we swallow errors because there's nothing
  // useful to show the user in a page-unload handler.
  useEffect(() => () => {
    if (flushTimer.current) clearTimeout(flushTimer.current);
  }, []);

  // Reading-level copy adaptation. When level is 'insider' (or the ws is
  // still loading), we render originals. Otherwise we ask the backend for
  // adapted versions of every rewritable passage; the backend caches so
  // subsequent visits are instant.
  useEffect(() => {
    if (!ws) return;
    if (readingLevel === "insider") { setAdaptedCopy({}); return; }
    let cancelled = false;
    setAdaptLoading(true);
    const fields = {};
    if (ws.purpose) fields["purpose"] = ws.purpose;
    (ws.sections || []).forEach((s, i) => {
      if (s.instructions) fields[`section_${i}_instructions`] = s.instructions;
      if (s.thinking_prompt) fields[`section_${i}_thinking`] = s.thinking_prompt;
    });
    if (ws.decision_gate?.question)
      fields["decision_question"] = ws.decision_gate.question;
    if (Object.keys(fields).length === 0) {
      setAdaptLoading(false);
      return;
    }
    adaptCopy({
      source: "worksheet",
      sourceId: worksheetId,
      level: readingLevel,
      fields,
    })
      .then((adapted) => {
        if (!cancelled) setAdaptedCopy(adapted || {});
      })
      .catch(() => { if (!cancelled) setAdaptedCopy({}); })
      .finally(() => { if (!cancelled) setAdaptLoading(false); });
    return () => { cancelled = true; };
  }, [ws, readingLevel, worksheetId]);

  // Small helper — returns adapted text when the reader isn't in Insider
  // mode; falls back to the original if the adaptation call hasn't
  // completed or failed.
  const pick = (originalText, adaptedKey) =>
    readingLevel === "insider" ? originalText : (adaptedCopy[adaptedKey] || originalText);

  const setAnswer = (field, value) => {
    setAnswers((prev) => ({ ...prev, [field.field_id]: value }));
    pendingAnswers.current[field.field_id] = value;
    // Clear the highlight for this field once the user edits it.
    if (highlightMissing.includes(field.field_id)) {
      setHighlightMissing((prev) => prev.filter((id) => id !== field.field_id));
    }
    scheduleFlush();
  };

  const setNote = (fieldId, noteValue) => {
    setNotes((prev) => ({ ...prev, [fieldId]: noteValue }));
    pendingNotes.current[fieldId] = noteValue;
    scheduleFlush();
  };

  const setDecisionValue = (patch) => {
    setDecision(patch);
    pendingDecision.current = patch;
    if (highlightMissing.includes("__decision_gate__")) {
      setHighlightMissing((prev) => prev.filter((id) => id !== "__decision_gate__"));
    }
    scheduleFlush();
  };

  const onBlurFlush = () => {
    if (flushTimer.current) clearTimeout(flushTimer.current);
    flush();
  };

  // Compute the "why disabled" tooltip and highlight list without waiting
  // for a submit round-trip. Uses the latest server-side `meta`.
  const submitBlockedReason = useMemo(() => {
    if (meta.status === "submitted") return "Already submitted";
    if (!meta.gate_complete) return "Complete the Decision Gate with a written commitment";
    if (meta.completion_pct < 80) {
      return `Answer more required fields (${meta.completion_pct}% / 80% needed)`;
    }
    return "";
  }, [meta]);

  const scrollToField = (fid) => {
    const el = document.getElementById(fid === "__decision_gate__" ? "decision-gate" : `f-${fid}`);
    if (el) el.scrollIntoView({ behavior: "smooth", block: "center" });
  };

  const onSubmit = async () => {
    setSubmitting(true);
    try {
      // Flush pending edits so the server scores the latest state.
      if (flushTimer.current) clearTimeout(flushTimer.current);
      await flush();
      const doc = await submitResponse(worksheetId);
      setMeta({
        status: "submitted",
        completion_pct: doc.completion_pct,
        required_missing: doc.required_missing || [],
        word_count: doc.word_count || 0,
        gate_complete: !!doc.gate_complete,
        submitted_at: doc.submitted_at || null,
      });
      toast.success("Worksheet submitted");
    } catch (e) {
      const detail = e?.response?.data?.detail;
      if (detail && typeof detail === "object") {
        setMeta((m) => ({ ...m,
          required_missing: detail.required_missing || m.required_missing,
          completion_pct: detail.completion_pct ?? m.completion_pct,
          gate_complete: !!detail.gate_complete,
        }));
        setHighlightMissing(detail.required_missing || []);
        toast.error(detail.message || "Submit blocked");
        const first = (detail.required_missing || [])[0];
        if (first) setTimeout(() => scrollToField(first), 250);
      } else {
        toast.error(formatError(e));
      }
    } finally {
      setSubmitting(false);
    }
  };

  if (error) return (
    <AppShell title="Worksheet">
      <div className="surface p-10 text-center text-muted-foreground">{error}</div>
    </AppShell>
  );
  if (!ws) return (
    <AppShell title="Worksheet">
      <div className="flex justify-center py-20">
        <Loader2 className="w-6 h-6 animate-spin text-muted-foreground" />
      </div>
    </AppShell>
  );

  const nvg = ws._nav || {};
  const totalFields = (ws.sections || []).reduce(
    (a, s) => a + (s.fields || []).length, 0);
  const isBonus = String(ws.module_id || "").startsWith("bonus-");

  return (
    <AppShell title={ws.title}>
      <div className="w-full pb-32">
        {/* Header — title, back link, and progress ring */}
        <div className="flex items-start justify-between gap-6 mb-6">
          <div className="min-w-0">
            {/* Route back to the specific module the worksheet belongs
                to when we have a slug; bonus modules (no detail page)
                fall back to the workbook index. */}
            <Link
              to={ws.module_slug ? `/modules/${ws.module_slug}` : "/workbook"}
              className="inline-flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground"
              data-testid="back-to-module"
            >
              <ArrowLeft className="w-3.5 h-3.5" />
              {ws.module_slug ? "Back to module" : "Back to workbook"}
            </Link>
            <div className="mt-2 flex items-center gap-2">
              {isBonus && (
                <span className="text-[10px] uppercase tracking-widest px-2 py-0.5 rounded-full border border-purple-400/40 text-purple-200 bg-purple-500/10">
                  Bonus module
                </span>
              )}
              <div className="text-xs text-muted-foreground">
                Module {ws.module_number} · {ws.module_title}
              </div>
            </div>
            <h1 className="mt-2 font-display text-3xl sm:text-4xl leading-tight">
              {ws.title}
            </h1>
          </div>
          <div className="flex items-center gap-3 shrink-0">
            <div className="text-right">
              <div className="text-[10px] uppercase tracking-widest text-muted-foreground">
                {meta.status === "submitted" ? "Submitted" : "Progress"}
              </div>
              <SavedIndicator state={autosave} />
            </div>
            <ProgressRing value={meta.completion_pct} size={64} stroke={6} />
          </div>
        </div>

        {/* Purpose paragraph — long-form intro copy that anchors why this
            worksheet exists. Rendered as a Blueprint-style card: eyebrow +
            structured paragraphs (auto-split at sentence boundaries when a
            single paragraph exceeds ~350 characters). Preserves author
            paragraph breaks in the source. */}
        <section className="rounded-2xl border border-border bg-white p-6 sm:p-8 mb-6 relative group shadow-sm" data-testid="worksheet-purpose-card">
          {adaptLoading && readingLevel !== "insider" && (
            <div className="absolute top-3 right-3 inline-flex items-center gap-1 text-[10px] uppercase tracking-widest text-shell-accent bg-shell-accent/10 rounded-full px-2 py-0.5" data-testid="adapt-loading">
              <Loader2 className="w-3 h-3 animate-spin" /> Adapting…
            </div>
          )}
          {readingLevel !== "insider" && !adaptLoading && (
            <div className="absolute top-3 right-3 opacity-0 group-hover:opacity-100 transition">
              <RegenerateChip
                source="worksheet" sourceId={worksheetId}
                field="purpose" level={readingLevel}
                originalText={ws.purpose}
                onDone={(text) => setAdaptedCopy((prev) => ({ ...prev, purpose: text }))}
              />
            </div>
          )}
          <p className="text-[10px] uppercase tracking-[0.2em] font-bold text-shell-accent inline-flex items-center gap-1.5">
            <Sparkles className="w-3 h-3" /> Purpose
          </p>
          <div className="mt-3 space-y-4" data-testid="worksheet-purpose">
            {splitIntroCopy(pick(ws.purpose, "purpose")).map((para, i) => (
              <p
                key={i}
                className={`text-[15px] leading-relaxed text-foreground/90 ${
                  i === 0 ? "text-[16px] font-medium text-foreground" : ""
                }`}
              >
                {para}
              </p>
            ))}
          </div>
        </section>

        {/* Metadata strip */}
        <section className="grid grid-cols-2 sm:grid-cols-4 gap-4 p-4 rounded-xl border border-border bg-white mb-8">
          <MetaPill icon={Clock} label="Estimated time" value={ws.estimated_time} />
          <MetaPill icon={Sparkles} label="Difficulty" value={ws.difficulty} />
          <MetaPill icon={Target} label="Outcome" value={ws.outcome} />
          <MetaPill icon={Link2} label="Connects to" value={ws.connects_to} />
        </section>

        {/* Before you start */}
        {(ws.inputs_needed || []).length > 0 && (
          <section className="mb-10">
            <h3 className="text-xs uppercase tracking-widest text-muted-foreground mb-3">
              Before you start
            </h3>
            <ul className="space-y-2">
              {ws.inputs_needed.map((line, i) => (
                <li key={i} className="flex items-start gap-2 text-sm text-foreground/90">
                  <span className="mt-2 w-1.5 h-1.5 rounded-full bg-[color:var(--brand-accent,#4FC0E7)] shrink-0" />
                  <span>{line}</span>
                </li>
              ))}
            </ul>
          </section>
        )}

        {/* Sections */}
        {(ws.sections || []).map((section, si) => (
          <section key={section.section_id || si} className="mb-10">
            <div className="mb-4">
              <div className="text-[10px] uppercase tracking-widest text-muted-foreground/85">
                Section {si + 1}
              </div>
              <h2 className="mt-1 font-display text-2xl leading-tight">
                {section.title}
              </h2>
              {section.instructions && (
                <p className="mt-2 text-sm italic text-muted-foreground leading-relaxed max-w-3xl">
                  {pick(section.instructions, `section_${si}_instructions`)}
                </p>
              )}
            </div>

            <div className="space-y-6">
              {(section.fields || []).map((field) => (
                <FieldRenderer
                  key={field.field_id}
                  field={field}
                  value={answers[field.field_id]}
                  onChange={(v) => setAnswer(field, v)}
                  onBlur={onBlurFlush}
                  highlight={highlightMissing.includes(field.field_id)}
                  note={notes[field.field_id] || ""}
                  onChangeNote={(v) => setNote(field.field_id, v)}
                  onNoteBlur={onBlurFlush}
                />
              ))}
            </div>

            {section.thinking_prompt && (
              <div className="mt-6 rounded-lg border border-sky-400/40 bg-sky-50 p-4 sm:p-5 relative group">
                {readingLevel !== "insider" && (
                  <div className="absolute top-3 right-3 opacity-0 group-hover:opacity-100 transition">
                    <RegenerateChip
                      source="worksheet" sourceId={worksheetId}
                      field={`section_${si}_thinking`} level={readingLevel}
                      originalText={section.thinking_prompt}
                      onDone={(text) => setAdaptedCopy((prev) => ({ ...prev, [`section_${si}_thinking`]: text }))}
                    />
                  </div>
                )}
                <div className="flex items-center gap-2 mb-2 text-[10px] uppercase tracking-widest text-sky-700 font-semibold">
                  <Lightbulb className="w-3.5 h-3.5" /> Think about this
                </div>
                <p className="text-sm text-sky-900 leading-relaxed">
                  {pick(section.thinking_prompt, `section_${si}_thinking`)}
                </p>
              </div>
            )}
          </section>
        ))}

        {/* Decision Gate */}
        <DecisionGate
          gate={
            ws.decision_gate && readingLevel !== "insider" && adaptedCopy.decision_question
              ? { ...ws.decision_gate, question: adaptedCopy.decision_question }
              : ws.decision_gate
          }
          value={decision}
          onChange={setDecisionValue}
          onBlur={onBlurFlush}
          highlight={highlightMissing.includes("__decision_gate__")}
        />

        {/* Submit + Nav */}
        <div className="mt-10 rounded-xl border border-border bg-white p-5 flex flex-col sm:flex-row gap-4 items-center justify-between">
          <div className="text-xs text-muted-foreground">
            {totalFields} fields · {meta.word_count} words written · {meta.completion_pct}% complete
          </div>
          <div className="flex items-center gap-3">
            <button
              type="button"
              onClick={onSubmit}
              disabled={!!submitBlockedReason || submitting}
              title={submitBlockedReason || "Submit this worksheet"}
              className="btn-primary px-5 disabled:opacity-40 disabled:cursor-not-allowed inline-flex items-center gap-2"
              data-testid="submit-worksheet"
            >
              {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : null}
              {meta.status === "submitted" ? "Submitted" : "Submit worksheet"}
            </button>
          </div>
        </div>

        {/* Prev / next */}
        <div className="mt-8 flex items-center justify-between gap-3">
          <button
            type="button"
            onClick={() => nvg.prev && nav(`/workbook/${nvg.prev.worksheet_id}`)}
            disabled={!nvg.prev}
            className="inline-flex items-center gap-2 text-sm text-foreground/80 hover:text-foreground disabled:opacity-30 disabled:cursor-not-allowed max-w-[45%] text-left"
            data-testid="nav-prev-worksheet"
          >
            <ArrowLeftFromLine className="w-4 h-4 shrink-0" />
            <span className="min-w-0">
              <span className="block text-[10px] uppercase tracking-widest text-muted-foreground/70">Previous</span>
              <span className="block truncate">{nvg.prev?.title || "Start of the workbook"}</span>
            </span>
          </button>
          <button
            type="button"
            onClick={() => nvg.next && nav(`/workbook/${nvg.next.worksheet_id}`)}
            disabled={!nvg.next}
            className="inline-flex items-center gap-2 text-sm text-foreground/80 hover:text-foreground disabled:opacity-30 disabled:cursor-not-allowed max-w-[45%] text-right"
            data-testid="nav-next-worksheet"
          >
            <span className="min-w-0">
              <span className="block text-[10px] uppercase tracking-widest text-muted-foreground/70">Next</span>
              <span className="block truncate">{nvg.next?.title || "End of the workbook"}</span>
            </span>
            <ArrowRight className="w-4 h-4 shrink-0" />
          </button>
        </div>
      </div>
    </AppShell>
  );
}
